import { z } from 'zod';
import { idSchema, isoDateTimeSchema, tenantIdSchema } from './common.schema';
/**
 * Cross-tenant management delegation (#16; docs/architecture/delegation.md).
 *
 * A standalone business keeps its own tenant (the GRANTOR). A managing service provider's tenant
 * (the GRANTEE) receives a revocable, scoped grant to act in it: no data migration, reversible when
 * the business changes provider, and the business keeps ownership and visibility. No grant, no access.
 *
 * One document per (grantor, grantee) pair, id `${grantor}~${grantee}`, so a pair can never hold two
 * live grants. History (every transition) lives in the audit log of both tenants.
 *
 * Tenant isolation note: this is a PLATFORM-level collection (like the Platform Directory), because a
 * delegation belongs to two tenants. Every read names the tenant it is acting for and the document's
 * own parties are re-checked by the code that uses it.
 */
/** The ceiling a business can grant. `account_admin` is deliberately absent: the business always keeps
 *  control of its own membership, API credentials and the delegation itself. */
export const DELEGATION_MAX_ROLES = ['viewer', 'agent', 'supervisor', 'admin'];
export const DELEGATION_STATUSES = ['pending', 'active', 'declined', 'revoked'];
export const DELEGATION_SIDES = ['grantor', 'grantee'];
/** `~` cannot occur in a tenant id (letters, digits, `_`, `-`), so the encoding is unambiguous: no two pairs share an id. */
export const delegationId = (grantorTenantId, granteeTenantId) => `${grantorTenantId}~${granteeTenantId}`;
/** The most clients one delegation can name. The API refuses more; the SPA stops the selection at this number. */
export const DELEGATION_MAX_CLIENT_IDS = 500;
/** A client scope is a SET of the grantor's clients; null = every client. `[]` would be a dead grant. */
export const delegationClientIdsSchema = z
    .array(idSchema)
    .min(1)
    .max(DELEGATION_MAX_CLIENT_IDS)
    .refine((ids) => new Set(ids).size === ids.length, { message: 'clientIds must be unique' })
    .nullable();
/** Request: open a delegation from the acting tenant's side. */
export const delegationProposeSchema = z
    .object({
    /** The other party's tenant id. */
    counterpartTenantId: tenantIdSchema,
    /** Which side the ACTING tenant is: 'grantor' = "I am the business, let this provider manage me". */
    as: z.enum(DELEGATION_SIDES),
    /** Required when proposing as the grantor (the business decides what it gives). */
    maxRole: z.enum(DELEGATION_MAX_ROLES).optional(),
    clientIds: delegationClientIdsSchema.optional(),
})
    .strict()
    .superRefine((val, ctx) => {
    if (val.as === 'grantor' && !val.maxRole) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['maxRole'], message: 'maxRole is required when proposing as the grantor' });
    }
    // The scope is explicit: `null` means every client, and must be chosen, never defaulted.
    if (val.as === 'grantor' && val.clientIds === undefined) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['clientIds'], message: 'clientIds is required (null = all clients)' });
    }
    if (val.as === 'grantee' && (val.maxRole !== undefined || val.clientIds !== undefined)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['maxRole'],
            message: 'only the grantor (the business) sets maxRole and clientIds',
        });
    }
});
/** Request: the responding side accepts. The grantor, when responding, supplies the terms. */
export const delegationAcceptSchema = z
    .object({
    maxRole: z.enum(DELEGATION_MAX_ROLES).optional(),
    clientIds: delegationClientIdsSchema.optional(),
})
    .strict();
export const tenantDelegationSchema = z
    .object({
    /** The data owner (the business). Doubles as the tenantId audit entries are written under. */
    grantorTenantId: tenantIdSchema,
    /** The tenant whose members may act (the provider). */
    granteeTenantId: tenantIdSchema,
    /** Who opened it; the OTHER side responds. */
    initiatedBy: z.enum(DELEGATION_SIDES),
    status: z.enum(DELEGATION_STATUSES),
    /** Set by the grantor; null only while a grantee-initiated proposal awaits the business's terms. */
    maxRole: z.enum(DELEGATION_MAX_ROLES).nullable(),
    /** The grantor's clients the grantee may see/act on; null = all. Meaningful only with maxRole. */
    clientIds: delegationClientIdsSchema,
    proposedAt: isoDateTimeSchema,
    proposedBy: idSchema,
    decidedAt: isoDateTimeSchema.nullable(),
    decidedBy: idSchema.nullable(),
    revokedAt: isoDateTimeSchema.nullable(),
    revokedBy: idSchema.nullable(),
    revokedBySide: z.enum(DELEGATION_SIDES).nullable(),
    updatedAt: isoDateTimeSchema,
})
    .strict()
    .superRefine((val, ctx) => {
    if (val.grantorTenantId === val.granteeTenantId) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['granteeTenantId'], message: 'a tenant cannot delegate to itself' });
    }
    if (val.status === 'active' && val.maxRole === null) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['maxRole'], message: 'an active delegation has terms' });
    }
    if (val.status === 'active' && (val.decidedAt === null || val.decidedBy === null)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['decidedAt'], message: 'an active delegation was accepted by someone' });
    }
    if (val.status === 'revoked' && (val.revokedAt === null || val.revokedBy === null || val.revokedBySide === null)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['revokedAt'], message: 'a revoked delegation records who revoked it' });
    }
});
//# sourceMappingURL=delegation.schema.js.map